import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GREEN, INK, LINE, MUTED } from '../../home/homeTheme';

// Finestre della scheda Report Mapping: stessi campi, testi e comportamenti della pagina classica.
const dialogPaper = { sx: { borderRadius: 3 } };
const titleSx = { fontWeight: 800 };
const actionsSx = { px: 3, pb: 2 };
const primarySx = { textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN, minWidth: 96 };
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-subtle)', borderBottom: `1px solid ${LINE}` };
const cellSx = { fontSize: 13, color: INK, borderBottom: `1px solid ${LINE}` };

/** Campi di un foglio (File URL, File Name e Ticketing System o Data Structure), per la finestra e per la modifica nella riga. */
export function SheetMappingFields({ m, inline }) {
  const isSla = m.mappingDialogType === 'sla';
  const fieldSx = inline ? { bgcolor: 'var(--rt-paper)' } : undefined;
  return (
    <>
      <TextField
        name="file_url"
        label="File URL"
        value={m.mappingDialogForm.file_url}
        onChange={m.handleMappingDialogChange}
        required
        fullWidth
        size="small"
        placeholder="https://docs.google.com/spreadsheets/..."
        autoFocus={inline}
        sx={fieldSx}
      />
      <TextField
        name="file_name"
        label="File Name"
        value={m.mappingDialogForm.file_name}
        onChange={m.handleMappingDialogChange}
        required
        fullWidth
        size="small"
        placeholder="e.g. Incident_Q2_2026"
        sx={fieldSx}
      />
      {isSla ? (
        <TextField
          select
          name="data_structure"
          label="Data Structure"
          value={m.mappingDialogForm.data_structure}
          onChange={m.handleMappingDialogChange}
          required
          fullWidth
          size="small"
          disabled={!!m.editingSheetMappingId}
          helperText={m.editingSheetMappingId ? 'Data Structure cannot be changed after creation.' : undefined}
          sx={fieldSx}
        >
          {m.dataStructureOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField select name="ticketing_system" label="Ticketing System" value={m.mappingDialogForm.ticketing_system} onChange={m.handleMappingDialogChange} required fullWidth size="small" sx={fieldSx}>
          {m.ticketingOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    </>
  );
}

/** Aggiunta di un foglio (Field o SLA). La modifica di un foglio esistente si apre nella sua riga (SheetInlineEditor). */
export function SheetMappingDialog({ m }) {
  const isSla = m.mappingDialogType === 'sla';
  return (
    <Dialog open={m.mappingDialogOpen && !m.editingSheetMappingId} onClose={m.handleCloseMappingDialog} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
      <DialogTitle sx={titleSx}>{isSla ? 'Add SLA Mapping' : 'Add Field Mapping'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {m.mappingDialogError && <Alert severity="error">{m.mappingDialogError}</Alert>}
          <SheetMappingFields m={m} />
        </Box>
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button color="inherit" onClick={m.handleCloseMappingDialog} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={m.handleSaveMappingDialog} disabled={m.mappingSaving} sx={primarySx}>
          {m.mappingSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Modifica di un foglio direttamente sotto la sua riga: stessi campi, stessi controlli e stesso salvataggio
 * della finestra classica "Edit Field / SLA Mapping". Invio salva, Esc annulla.
 */
export function SheetInlineEditor({ m }) {
  const isSla = m.mappingDialogType === 'sla';
  const onKeyDown = (e) => {
    if (e.key === 'Escape') m.handleCloseMappingDialog();
    // Invio salva, tranne quando è aperto un menu di scelta
    if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type !== 'hidden' && !m.mappingSaving) m.handleSaveMappingDialog();
  };
  return (
    <Box onKeyDown={onKeyDown} sx={{ p: 2, bgcolor: 'var(--rt-subtle)', borderBottom: `1px solid ${LINE}`, boxShadow: `inset 3px 0 0 ${GREEN}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
        {isSla ? 'Edit SLA Mapping' : 'Edit Field Mapping'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1fr)' }, gap: 1.5, alignItems: 'start' }}>
        <SheetMappingFields m={m} inline />
      </Box>
      {m.mappingDialogError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {m.mappingDialogError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
        <Button color="inherit" onClick={m.handleCloseMappingDialog} disabled={m.mappingSaving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={m.handleSaveMappingDialog} disabled={m.mappingSaving} sx={primarySx}>
          {m.mappingSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

/** Mapping Values di un campo con logica "Mapping": valore normalizzato ↔ valore del foglio. */
export function MappingValuesDialog({ m }) {
  const canEdit = m.savedIsMasterService;
  const resetForm = () => m.setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' });
  return (
    <Dialog open={m.mappingValuesDialogOpen} onClose={() => m.setMappingValuesDialogOpen(false)} maxWidth="md" fullWidth PaperProps={dialogPaper}>
      <DialogTitle sx={titleSx}>
        Mapping Values
        {m.selectedConfigRowForValues ? (
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 500, color: MUTED }}>
            {' '}
            — {m.selectedConfigRowForValues.label}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {m.mappingValuesError && <Alert severity="error">{m.mappingValuesError}</Alert>}
          {m.mappingValuesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={18} color="success" />
              <Typography variant="body2">Loading mapping values...</Typography>
            </Box>
          ) : (
            <>
              {canEdit && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', p: 1.5, borderRadius: 2.5, bgcolor: 'var(--rt-subtle)', border: `1px solid ${LINE}` }}>
                  <TextField
                    select
                    size="small"
                    label="Normalized Value"
                    value={m.mappingValueForm.normalized_ref_id}
                    onChange={(e) => m.setMappingValueForm((prev) => ({ ...prev, normalized_ref_id: e.target.value }))}
                    sx={{ minWidth: 240, bgcolor: 'var(--rt-paper)' }}
                  >
                    {m.normalizedOptionsForValues.map((opt) => (
                      <MenuItem key={opt.id} value={String(opt.id)}>
                        {m.formatLookupWithGroup(opt)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Source Value"
                    value={m.mappingValueForm.source_value}
                    onChange={(e) => m.setMappingValueForm((prev) => ({ ...prev, source_value: e.target.value }))}
                    sx={{ minWidth: 240, flex: 1, bgcolor: 'var(--rt-paper)' }}
                  />
                  <Button variant="contained" color="success" onClick={m.handleSaveMappingValue} sx={primarySx}>
                    {m.mappingValueForm.id ? 'Update' : 'Add'}
                  </Button>
                  {m.mappingValueForm.id && (
                    <Button color="inherit" onClick={resetForm} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                  )}
                </Box>
              )}

              <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2.5, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Normalized Value</TableCell>
                      <TableCell sx={headSx}>Source Value</TableCell>
                      {canEdit && <TableCell sx={{ ...headSx, width: 160 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {m.mappingValues.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 3 : 2} sx={{ ...cellSx, textAlign: 'center', color: MUTED, py: 2.5 }}>
                          No mapping values yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      m.mappingValues.map((item) => {
                        const match = m.normalizedOptionsForValues.find((x) => x.id === item.normalized_ref_id);
                        const editingThis = m.mappingValueForm.id === item.id;
                        return (
                          <TableRow key={item.id} sx={editingThis ? { bgcolor: 'var(--rt-green-soft)' } : undefined}>
                            <TableCell sx={cellSx}>{match ? m.formatLookupWithGroup(match) : item.normalized_ref_id}</TableCell>
                            <TableCell sx={cellSx}>{item.source_value}</TableCell>
                            {canEdit && (
                              <TableCell sx={cellSx}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button size="small" color="inherit" onClick={() => m.handleEditMappingValue(item)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    Edit
                                  </Button>
                                  <Button size="small" color="error" onClick={() => m.handleDeleteMappingValue(item.id)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    Delete
                                  </Button>
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button color="inherit" onClick={() => m.setMappingValuesDialogOpen(false)} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Guida al formato (logica "Format"). */
export function FormatHelpDialog({ m }) {
  const rows = [
    ['YYYY', 'Years (es. 2026).'],
    ['MM', 'Month (01-12).'],
    ['DD', 'Days (01-31).'],
    ['HH', 'hours 24h (00-23).'],
    ['MM', 'Minutes (00-59).'],
    ['SS', 'Seconds (00-59).'],
    ['XXX', 'decimals'],
  ];
  const code = { fontFamily: 'monospace', fontSize: 12.5, bgcolor: 'var(--rt-track)', color: INK, px: 0.75, py: 0.1, borderRadius: 1 };
  return (
    <Dialog open={m.formatHelpOpen} onClose={() => m.setFormatHelpOpen(false)} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
      <DialogTitle sx={titleSx}>Format Help</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Please insert the data format as appears in gsheet, checking the real value of the cell by clicking on it.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Write the pattern following this naming convention
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.75, alignItems: 'center', mb: 2 }}>
          {rows.map(([token, meaning], i) => (
            <React.Fragment key={`${token}-${i}`}>
              <Box component="span" sx={{ ...code, justifySelf: 'start' }}>
                {token}
              </Box>
              <Typography variant="body2">{meaning}</Typography>
            </React.Fragment>
          ))}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Example
        </Typography>
        <Box component="span" sx={code}>
          YYYY-MM-DD HH:MM:SS.XXX
        </Box>
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button color="inherit" onClick={() => m.setFormatHelpOpen(false)} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Conferma generica (eliminazione foglio, chiusura con modifiche non salvate). */
export function ConfirmMappingDialog({ m }) {
  return (
    <Dialog open={m.confirmDialogOpen} onClose={m.handleConfirmDialogClose} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
      <DialogTitle sx={titleSx}>{m.confirmDialogTitle || 'Confirm action'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{m.confirmDialogMessage}</Typography>
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button color="inherit" onClick={m.handleConfirmDialogClose} disabled={m.confirmDialogBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={m.handleConfirmDialogConfirm} disabled={m.confirmDialogBusy} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
          {m.confirmDialogBusy ? 'Please wait...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Messaggi della scheda (in basso a destra, come nella pagina classica). */
export function MappingToast({ m }) {
  const close = () => m.setMappingToast((prev) => ({ ...prev, open: false }));
  return (
    <Snackbar open={m.mappingToast.open} autoHideDuration={3000} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Alert onClose={close} severity={m.mappingToast.severity} variant="filled" sx={{ width: '100%' }}>
        {m.mappingToast.message}
      </Alert>
    </Snackbar>
  );
}
